import { ApolloClient, gql } from '@apollo/client'
import type { TShared } from 'translations'
import { z } from 'zod'

import * as Routes from 'const/routes'

import type { MultiSelectAdaptiveProps as Props } from '../MultiSelectAdaptive'
import { MultiSelectAdaptivePresetsSitesGetSites } from './PresetSites/MultiSelectAdaptivePresetsSitesGetSites.generated'
import { ValueLabelDefault } from './ValueLabelDefault'

const NextTokenSchema = z.object({
  type: z.string(),
  siteNameSortKey: z.string(),
})

export const PresetSites = ({
  tShared,
  apolloClient,
}: {
  tShared: TShared
  apolloClient: ApolloClient<object>
}) => {
  return {
    options: {
      fetcher: async ({ options, optionsMap, nextToken: nextTokenUnknown }) => {
        const $nextToken = NextTokenSchema.safeParse(nextTokenUnknown)
        const nextToken = $nextToken.success ? $nextToken.data : undefined
        const response = await (async () => {
          try {
            const response = await apolloClient.query({
              query: MultiSelectAdaptivePresetsSitesGetSites,
              variables: { nextToken: nextToken ?? null },
              fetchPolicy: 'network-only',
            })
            if (response.error) {
              return undefined
            }
            return response
          } catch {
            return undefined
          }
        })()

        if (!response) {
          return { options, error: true }
        }

        const newOptions = options.concat(
          response.data.getSites.sites.reduce((acc, site) => {
            if (site && !optionsMap.has(site.id)) {
              acc.push({ value: site.id, label: site.name })
            }
            return acc
          }, [] as Props.Option[])
        )

        const newNextToken = response.data.getSites.nextToken ?? undefined

        return { options: newOptions, nextToken: newNextToken }
      },
      initialOptions: {
        fetcher: async ({ value, optionsMap }) => {
          const missingSiteIds = Array.isArray(value)
            ? value.filter((id) => !optionsMap.get(id))
            : []

          if (!missingSiteIds.length) {
            return undefined
          }

          // Send a single query to fetch N sites.
          const query = gql(`query MultiSelectAdaptivePresetsSitesGetSitesById {
${missingSiteIds.map(
  (siteId, index) =>
    `getSite${index}: getSite(siteUuid: "${siteId}") { id name }`
)} }`)

          type MultiSelectAdaptivePresetsSitesGetSitesByIdQueryResponse =
            Record<
              `getSite${string}`,
              {
                id: string
                name: string
              }
            >

          const response = await (async () => {
            try {
              const response = await apolloClient.query({ query })
              if (response.error) {
                return undefined
              }
              return response
            } catch {
              return undefined
            }
          })()

          if (!response) {
            return { error: true }
          }

          const dataUnsafe =
            response.data as MultiSelectAdaptivePresetsSitesGetSitesByIdQueryResponse
          const options: Props.Option[] = Object.keys(dataUnsafe).map(
            (_, index) => {
              const site = dataUnsafe[`getSite${index}`]
              return { value: site?.id, label: site?.name }
            }
          )

          return { options }
        },
      },
      errorMessage: tShared('failedToLoadSites'),
    },
    label: tShared('assignSites'),
    valueLabel: ValueLabelDefault({
      tShared,
      allKey: 'allSites',
      manyKey: 'nSites',
      noneKey: 'noSitesAssigned',
    }),
    selectAll: {
      label: tShared('allSites'),
    },
    search: {
      filter: true,
    },
    emptyMessage: tShared(
      'youHaveNotCreatedAnySitesForYourBusinessCreateOneFirstToContinue'
    ),
    emptyActions: [
      {
        label: tShared('createASite'),
        href: Routes.ROOT.PORTAL.PAYMENTS.SITES.CREATE_SITE.path,
      },
    ],
  } satisfies Partial<Props>
}
